import {
  Children,
  type ComponentPropsWithoutRef,
  type ReactNode,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useId,
  useMemo,
} from "react";
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import { Input, type InputProps } from "../atoms/input.js";
import { Label } from "../atoms/label.js";
import { cx } from "../internal/cx.js";

interface FormFieldContextValue {
  readonly id: string;
  readonly name: string;
  readonly errorId: string;
  readonly hintId: string;
  readonly errorMessage: string | undefined;
  readonly required: boolean;
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null);

function useFormFieldContext(): FormFieldContextValue {
  const ctx = useContext(FormFieldContext);
  if (!ctx) {
    throw new Error(
      "FormField subcomponents must be used inside a <FormField>. " +
        "Wrap your field markup in <FormField name=...>.",
    );
  }
  return ctx;
}

export interface FormFieldProps {
  /** Identifier under react-hook-form. Optional for controlled usage where
   * the surrounding form is not driven by react-hook-form. */
  readonly name?: string;
  /** Optional override; defaults to a `useId()`-derived value. */
  readonly id?: string;
  /** Convenience: when set, renders a `<Label>` above `children`. Lets
   * controlled callers skip the compound `<FormField.Label>` child. */
  readonly label?: ReactNode;
  /** Convenience alias accepted alongside `id` for callers ported from
   * older form APIs. Ignored when `id` is also provided. */
  readonly htmlFor?: string;
  readonly required?: boolean;
  /** Server-side error to merge in alongside react-hook-form's local errors. */
  readonly error?: string;
  readonly className?: string;
  readonly children: ReactNode;
}

/**
 * Compound field wrapper. Owns the unique input id, label association,
 * `aria-invalid`, `aria-describedby`, and required-marker. Children compose:
 *
 * ```tsx
 * <FormField name="firstName" required>
 *   <FormField.Label>First name</FormField.Label>
 *   <FormField.Input placeholder="Jane" />
 *   <FormField.Hint>As shown on your ID.</FormField.Hint>
 *   <FormField.Error />
 * </FormField>
 * ```
 *
 * Pairs natively with `react-hook-form`: `<FormField.Input />` reads the
 * surrounding `<FormProvider>` and registers `name` automatically. For
 * non-form usage (controlled), pass `value`/`onChange` directly to
 * `<FormField.Input registerWithForm={false} />`.
 */
export function FormField({
  name,
  id,
  htmlFor,
  label,
  required = false,
  error,
  className,
  children,
}: FormFieldProps) {
  const reactId = useId();
  const safeName = name ?? "";
  const slug = safeName ? safeName.replace(/[^a-zA-Z0-9_-]/g, "-") : "anon";
  const finalId = id ?? htmlFor ?? `field-${reactId}-${slug}`;
  const form = useFormContext();
  const localErrorMessage = error ?? extractErrorMessage(form?.formState?.errors, safeName);
  const ctx = useMemo<FormFieldContextValue>(
    () => ({
      id: finalId,
      name: safeName,
      errorId: `${finalId}-error`,
      hintId: `${finalId}-hint`,
      errorMessage: localErrorMessage,
      required,
    }),
    [finalId, safeName, localErrorMessage, required],
  );

  // The `error` prop is a convenience that auto-renders an error message for
  // callers using the props API. Callers using the compound API supply their
  // own `<FormField.Error />`; auto-rendering then would duplicate the alert
  // (two `<p role="alert">` with the same id), so suppress it in that case.
  const hasExplicitError = Children.toArray(children).some(
    (child) => isValidElement(child) && child.type === FormFieldError,
  );

  return (
    <FormFieldContext.Provider value={ctx}>
      <div className={cx("cw-v2-form-field", className)}>
        {label != null ? <FormFieldLabel>{label}</FormFieldLabel> : null}
        {children}
        {error && !hasExplicitError ? <FormFieldError /> : null}
      </div>
    </FormFieldContext.Provider>
  );
}

function FormFieldLabel({ children, ...rest }: ComponentPropsWithoutRef<typeof Label>) {
  const { id, required } = useFormFieldContext();
  return (
    <Label htmlFor={id} required={required} {...rest}>
      {children}
    </Label>
  );
}

interface FormFieldInputProps extends Omit<InputProps, "name" | "id" | "invalid"> {
  /** When true (default), the input registers itself with the surrounding
   * react-hook-form context. Set to false for fully-controlled usage. */
  readonly registerWithForm?: boolean;
}

const FormFieldInput = forwardRef<HTMLInputElement, FormFieldInputProps>(function FormFieldInput(
  { registerWithForm = true, onBlur, ...rest },
  ref,
) {
  const { id, name, errorMessage, required, errorId, hintId } = useFormFieldContext();
  const form = useFormContext();
  const invalid = Boolean(errorMessage);
  const describedBy = invalid ? errorId : hintId;
  if (registerWithForm && form) {
    const registration = form.register(name, { required });
    return (
      <Input
        {...registration}
        id={id}
        invalid={invalid}
        required={required}
        aria-describedby={describedBy}
        ref={(node) => {
          registration.ref(node);
          if (typeof ref === "function") ref(node);
          else if (ref) (ref as { current: HTMLInputElement | null }).current = node;
        }}
        onBlur={(e) => {
          registration.onBlur(e);
          onBlur?.(e);
        }}
        {...rest}
      />
    );
  }
  return (
    <Input
      ref={ref}
      id={id}
      name={name}
      invalid={invalid}
      required={required}
      aria-describedby={describedBy}
      onBlur={onBlur}
      {...rest}
    />
  );
});

function FormFieldError({ children }: { readonly children?: ReactNode }) {
  const { errorId, errorMessage } = useFormFieldContext();
  const message = children ?? errorMessage;
  if (!message) return null;
  return (
    <p
      id={errorId}
      role="alert"
      className="cw-v2-form-field__message cw-v2-form-field__message--error"
    >
      {message}
    </p>
  );
}

function FormFieldHint({ children }: { readonly children: ReactNode }) {
  const { hintId, errorMessage } = useFormFieldContext();
  if (errorMessage) return null;
  return (
    <p id={hintId} className="cw-v2-form-field__message">
      {children}
    </p>
  );
}

FormField.Label = FormFieldLabel;
FormField.Input = FormFieldInput;
FormField.Error = FormFieldError;
FormField.Hint = FormFieldHint;

/**
 * Render-prop variant that wraps `react-hook-form`'s `<Controller>`.
 * Use this when the underlying input doesn't accept `register()`'s
 * `onChange/onBlur/ref` shape (e.g., a Radix Select, a custom date picker).
 *
 * Mirrors `<Controller>`'s **two**-parameter generic signature
 * (`<TFieldValues, TName>`) so that `field.value` narrows to the specific
 * field's type rather than collapsing to the union of all form values.
 * Dropping `TName` here would force every caller to widen their handlers
 * (string | number | nested objects, etc.) — see the `forms.mdc`
 * "Field components" rule, which counts on per-field type narrowing.
 */
export function FormFieldController<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: ControllerProps<TFieldValues, TName>) {
  return <Controller {...props} />;
}

function extractErrorMessage(
  errors: Record<string, unknown> | undefined,
  name: string,
): string | undefined {
  if (!errors) return undefined;
  const segments = name.split(".");
  let cursor: unknown = errors;
  for (const segment of segments) {
    if (!cursor || typeof cursor !== "object") return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  if (cursor && typeof cursor === "object" && "message" in cursor) {
    const msg = (cursor as { message?: unknown }).message;
    return typeof msg === "string" ? msg : undefined;
  }
  return undefined;
}
